import { Injectable } from '@angular/core';

/**
 * Uploaded file info to include in API requests
 */
export interface UploadedFileInfo {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileExtension: string;
  s3Key: string;
  cdnUrl: string;
}

/**
 * File Upload Service
 *
 * Shared S3 mechanics for the two-step upload flow:
 *   1. Request a pre-signed URL from the API (entity-specific — lives on the
 *      owning service, e.g. DroneResourceService, because the endpoint and the
 *      S3 key prefix are scoped to the parent record)
 *   2. `uploadToS3()` — PUT the bytes straight to S3 with the pre-signed URL
 *   3. Register the row via the entity's API, passing s3Key + cdnUrl
 *
 * This service owns step 2 plus the file validation / display helpers. It
 * deliberately holds no presign methods: those differ per entity.
 *
 * Ported from american-drone-network-web's file-upload.service.ts.
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadService {

  /**
   * Upload a file to S3 using a pre-signed URL.
   *
   * Uses `fetch` rather than Angular's HttpClient on purpose: HttpClient runs
   * the auth interceptor, and the Firebase Bearer token must never be sent to
   * a third-party host. The interceptor currently URL-guards on
   * `environment.apiUrl`, but this doesn't depend on that staying true.
   *
   * @param file       the bytes to upload
   * @param uploadUrl  pre-signed PUT URL from the API
   * @param isPublic   pass false when the presign used `isPrivate: true`
   */
  async uploadToS3(file: File, uploadUrl: string, isPublic: boolean = true): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': file.type || 'application/octet-stream',
    };

    // DigitalOcean Spaces requires the ACL as a SIGNED HEADER, not a query
    // param — the Hub's getSignedUrl keeps it unhoisted for exactly this
    // reason. Omit it on a public presign and S3 rejects the signature.
    if (isPublic) {
      headers['x-amz-acl'] = 'public-read';
    }

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Check if a file is an allowed type.
   *
   * Keep this list in sync with the `accept` attribute on any file input that
   * feeds this service — `accept` only filters the OS picker and is bypassed
   * entirely by drag-and-drop, so this is the real gate.
   */
  isAllowedFileType(file: File): boolean {
    const allowedTypes = [
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      // PDFs
      'application/pdf',
      // Documents
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
    ];

    // Must match VALID_DRONE_RESOURCE_EXTENSIONS on the Hub and the `accept`
    // attribute on any input feeding this service.
    const allowedExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'webp',
      'pdf',
      'doc', 'docx', 'xls', 'xlsx', 'txt',
    ];

    const extension = this.getFileExtension(file.name);

    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
  }

  /**
   * Get max file size in bytes (100MB)
   */
  getMaxFileSize(): number {
    return 100 * 1024 * 1024; // 100MB
  }

  /**
   * Validate a file against the type and size rules. Returns null when the
   * file is acceptable, or a user-facing message explaining why it isn't.
   */
  validate(file: File): string | null {
    if (!this.isAllowedFileType(file)) {
      return 'That file type isn\'t supported.';
    }
    if (file.size > this.getMaxFileSize()) {
      return `File is too large (${this.formatFileSize(file.size)}). Max is ${this.formatFileSize(this.getMaxFileSize())}.`;
    }
    return null;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
}
